/**
 * @file useEnsureBrowserScroll.js
 * @description Hook para garantizar que el scroll del navegador esté siempre disponible
 * @created November 2025
 */

import { useEffect } from 'react';

/**
 * Hook para garantizar que el scroll del navegador esté siempre disponible
 * Sin importar qué componente se renderice o qué CSS se aplique
 * Optimizado para dispositivos móviles y desktop
 */
export const useEnsureBrowserScroll = () => {
  useEffect(() => {
    // Función para restaurar el scroll del navegador
    const ensureScroll = () => {
      // Detectar si es un dispositivo móvil
      const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Asegurar que el body tenga scroll disponible
      document.body.style.overflowY = 'auto';
      document.body.style.overflowX = 'hidden';
      document.body.style.scrollBehavior = 'smooth';
      
      // Asegurar que el html tenga scroll disponible
      document.documentElement.style.overflowY = 'auto';
      document.documentElement.style.overflowX = 'hidden';
      document.documentElement.style.scrollBehavior = 'smooth';
      
      // Optimizaciones específicas para móviles
      if (isMobile) {
        // Habilitar scroll suave en iOS
        document.body.style.webkitOverflowScrolling = 'touch';
        document.documentElement.style.webkitOverflowScrolling = 'touch';
        
        // Prevenir zoom accidental en iOS
        document.body.style.touchAction = 'manipulation';
        document.documentElement.style.touchAction = 'manipulation';
        
        // Asegurar que no hay altura fija que bloquee el scroll
        document.body.style.height = 'auto';
        document.body.style.minHeight = '100vh';
        document.documentElement.style.height = 'auto';
        document.documentElement.style.minHeight = '100vh';
      }
      
      // Añadir clase de utilidad
      document.body.classList.add('ensure-browser-scroll');
    };

    // Ejecutar inmediatamente
    ensureScroll();

    // Observer para detectar cambios en las clases del body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Si se pierde la clase, restaurarla
          if (!document.body.classList.contains('ensure-browser-scroll')) {
            document.body.classList.add('ensure-browser-scroll');
          }
        }
      });
    });

    // Observer para cambios en el style del body
    const styleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const bodyStyle = document.body.style;
          // Si se cambia overflow-y a hidden, restaurarlo
          if (bodyStyle.overflowY === 'hidden') {
            bodyStyle.overflowY = 'auto';
            if (window.innerWidth <= 768) {
              bodyStyle.webkitOverflowScrolling = 'touch';
            }
          }
        }
      });
    });

    // Listener para cambios de orientación en móviles
    const handleOrientationChange = () => {
      setTimeout(ensureScroll, 100); // Pequeño delay para que se complete el cambio
    };

    // Listener para redimensionado de ventana
    const handleResize = () => {
      ensureScroll();
    };

    // Inicializar observers y listeners
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    styleObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleResize);

    // Ejecutar cada vez que cambie la ruta (usando un timeout para asegurar que se aplique después de otros efectos)
    const timeout = setTimeout(ensureScroll, 100);

    // Cleanup
    return () => {
      observer.disconnect();
      styleObserver.disconnect();
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []); // Solo ejecutar una vez al montar el componente
};

export default useEnsureBrowserScroll;